import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ShopifyGetProduct from "../utils/ShopifyGetProduct";
import GetCustomizerImages from "../utils/GetCustomizerImages";
import ShopifyGetCustomer from "../utils/ShopifyGetCustomer";
import ParseProduct from "../utils/ParseProduct";
import getTokenByUserId from "../utils/GetTokenByUserId";
import GetColorSwatch from "../utils/GetColorSwatch";
import GetCustomer from "../utils/GetCustomer";
import useFetchBannerData from "../hooks/useFetchBannerData";
import useCacheSoundEffects from "../hooks/useCacheSoundEffects";
import useKioskMode from "../hooks/useKioskMode";
import useCharacterMode from "../hooks/useCharacterMode";

const DataContext = React.createContext(null);

const DataProvider = (props) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { bannerData } = useFetchBannerData();
  const audios = useCacheSoundEffects();
  const kioskData = useKioskMode();
  const characterData = useCharacterMode();
  const [token, setToken] = React.useState(null);
  const [customer, setCustomer] = React.useState(null);
  const [product, setProduct] = React.useState(null);
  const [variant, setVariant] = React.useState(null);
  const [swatches, setSwatches] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  // Get customer.
  React.useEffect(() => {
    const sessionToken = sessionStorage.getItem("sup_token");
    const urlStringId = searchParams.get("c");
    let customerId;

    try {
      customerId = (urlStringId && atob(urlStringId)) || searchParams.get("design")?.split("-")[0];
    } catch (e) {
      console.log(e);
    }

    if (!sessionToken && customerId) {
      getTokenByUserId(customerId).then((token) => {
        setToken(token);
      });
    } else if (sessionToken && customerId) {
      setToken(sessionToken);
    } else {
      sessionStorage.removeItem("sup_token");
    }
  }, []);

  React.useEffect(() => {
    if (token) {
      ShopifyGetCustomer(token).then((customer) => {
        const customerId = customer.id.replace("gid://shopify/Customer/", "");
        GetCustomer(customerId).then((value) => {
          setCustomer({ ...customer, lastCartId: value.lastCartId });
        });
      });
    } else {
      setCustomer(null);
    }
  }, [token]);

  // Get product, images.

  React.useEffect(() => {
    setLoading(true);
    Promise.allSettled([ShopifyGetProduct(params.productID), GetCustomizerImages(params.productID)])
      .then((results) => results.map((e) => e.value))
      .then(([product, images]) => {
        if (product) {
          const parsedProduct = ParseProduct(product, images);
          setProduct(parsedProduct);
        } else {
          setLoading(false);
        }
      });
  }, []);

  React.useEffect(() => {
    if (product) {
      Promise.allSettled(
        product.options.color.map((color) => GetColorSwatch(color.toLowerCase()))
      ).then((results) => {
        setSwatches(results.map((e) => e.value));
      });
    }
  }, [product?.options]);

  // Set variant.

  React.useEffect(() => {
    if (product) {
      const variant = product.variants.find((e) => e.id.includes(params.variantID));
      if (variant) {
        setVariant(variant);
      }
      setLoading(false);
    }
  }, [product, params.variantID]);

  const value = {
    token,
    customer,
    product,
    variant,
    loading,
    setToken,
    setLoading,
    setCustomer,
    bannerData,
    audios,
    ...kioskData,
    swatches,
    ...characterData,
  };

  return <DataContext.Provider value={value}>{props.children}</DataContext.Provider>;
};

export const useData = () => React.useContext(DataContext);

export default DataProvider;
